import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DiversityIcon from '@mui/icons-material/Diversity3';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageContainer, Section, StatGrid } from '../components/AdminContentWrapper';
import { DataContext } from '../../../context/DataContext';
import {
  listGroups,
  useSecurityGroupsService
} from '../../../services/SecurityGroupsService';
import { listRoles } from '../../../services/SecurityRolesService';
import { listPolicies } from '../../../services/SecurityPoliciesService';
import { listUsers } from '../../../services/SecurityUsersService';
import HelpIcon from '../../../components/help/HelpIcon';

const GROUP_ICONS = [
  <GroupsIcon sx={{ color: 'var(--wb-accent)' }} />,
  <PeopleAltIcon sx={{ color: 'var(--wb-success)' }} />,
  <DiversityIcon sx={{ color: 'var(--wb-warning)' }} />
];

const emptyFormState = { name: '', description: '', roleIds: [], policyIds: [] };

// Un Grupo de Usuario es una colección de usuarios (membresía vive en
// UserAccountTable.group_ids, ver SecurityUsers.jsx) a la que se le
// adjuntan Roles y/o Políticas directamente -- todos los miembros heredan
// esos permisos, además de los suyos propios (ver _compute_permissions en
// functions/security-roles/app.py). No hay grupos de sistema en V1.
const GroupFormDialog = ({
  open,
  mode,
  initialGroup,
  roles,
  rolesLoading,
  policies,
  policiesLoading,
  onClose,
  onSubmit,
  isSubmitting
}) => {
  const [formState, setFormState] = useState(emptyFormState);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === 'edit' && initialGroup) {
      setFormState({
        name: initialGroup.name || '',
        description: initialGroup.description || '',
        roleIds: Array.isArray(initialGroup.role_ids) ? [...initialGroup.role_ids] : [],
        policyIds: Array.isArray(initialGroup.policy_ids) ? [...initialGroup.policy_ids] : []
      });
    } else {
      setFormState(emptyFormState);
    }
    setError('');
  }, [open, mode, initialGroup]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formState.name.trim()) {
      setError('El nombre del grupo es obligatorio.');
      return;
    }
    setError('');
    await onSubmit(formState);
  };

  const selectSx = {
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-border-30)' },
    '& .MuiSvgIcon-root': { color: 'var(--wb-muted-80)' },
    color: 'var(--wb-text-title)',
    backgroundColor: 'var(--wb-bg-overlay-90)'
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        sx: {
          backgroundColor: 'var(--wb-bg-surface)',
          border: '1px solid var(--wb-border-16)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--wb-text-title)' }}>
        {mode === 'edit' ? 'Editar grupo' : 'Nuevo grupo'}
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'var(--wb-border)' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          margin="normal"
          label="Nombre"
          fullWidth
          value={formState.name}
          onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <HelpIcon module="seguridad" topic="grupos" anchor="nombre" />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiInputBase-root': { color: 'var(--wb-text-title)', backgroundColor: 'var(--wb-bg-overlay-90)' },
            '& .MuiInputLabel-root': { color: 'var(--wb-muted-80)' }
          }}
        />
        <TextField
          margin="normal"
          label="Descripción"
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          value={formState.description}
          onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <HelpIcon module="seguridad" topic="grupos" anchor="descripción" />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiInputBase-root': { color: 'var(--wb-text-title)', backgroundColor: 'var(--wb-bg-overlay-90)' },
            '& .MuiInputLabel-root': { color: 'var(--wb-muted-80)' }
          }}
        />

        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ color: 'var(--wb-muted-80)' }}>Roles adjuntos</InputLabel>
            <Select
              multiple
              value={formState.roleIds}
              onChange={(event) => setFormState((prev) => ({ ...prev, roleIds: event.target.value }))}
              input={<OutlinedInput label="Roles adjuntos" />}
              disabled={rolesLoading}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {selected.map((roleId) => (
                    <Chip key={roleId} label={roles.find((r) => r.role_id === roleId)?.name || roleId} size="small" />
                  ))}
                </Stack>
              )}
              sx={selectSx}
            >
              {roles.map((role) => (
                <MenuItem key={role.role_id} value={role.role_id}>
                  <Checkbox checked={formState.roleIds.includes(role.role_id)} />
                  <ListItemText primary={role.name} secondary={role.is_system ? 'Sistema' : undefined} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 3 }}>
            <HelpIcon module="seguridad" topic="grupos" anchor="roles-adjuntos" />
          </Box>
        </Stack>

        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ color: 'var(--wb-muted-80)' }}>Políticas adjuntas</InputLabel>
            <Select
              multiple
              value={formState.policyIds}
              onChange={(event) => setFormState((prev) => ({ ...prev, policyIds: event.target.value }))}
              input={<OutlinedInput label="Políticas adjuntas" />}
              disabled={policiesLoading}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {selected.map((policyId) => (
                    <Chip key={policyId} label={policies.find((p) => p.policy_id === policyId)?.name || policyId} size="small" />
                  ))}
                </Stack>
              )}
              sx={selectSx}
            >
              {policies.map((policy) => (
                <MenuItem key={policy.policy_id} value={policy.policy_id}>
                  <Checkbox checked={formState.policyIds.includes(policy.policy_id)} />
                  <ListItemText primary={policy.name} secondary={policy.is_system ? 'Sistema' : undefined} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 3 }}>
            <HelpIcon module="seguridad" topic="grupos" anchor="políticas-adjuntas" />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ color: 'var(--wb-muted)' }}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}
        >
          {isSubmitting ? 'Guardando...' : mode === 'edit' ? 'Guardar cambios' : 'Crear grupo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SecurityGroups = () => {
  const { isPlatformRoot, isTenantAdmin, userData } = useContext(DataContext);
  const canManageGroups = Boolean(isPlatformRoot || isTenantAdmin);
  const accessChecked = Boolean(userData);

  const { createGroup, updateGroup, deleteGroup } = useSecurityGroupsService();

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState('');

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);

  // Conteo de miembros por grupo: no hay endpoint dedicado (membresía
  // denormalizada en UserAccountTable.group_ids, ver plan) -- se cuenta en
  // memoria sobre el mismo listado que ya usa SecurityUsers.jsx.
  const [users, setUsers] = useState([]);

  const [feedback, setFeedback] = useState(null);
  const [dialogState, setDialogState] = useState({ open: false, mode: 'create', group: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchGroups = useCallback(async () => {
    if (!canManageGroups) {
      return;
    }
    setGroupsLoading(true);
    setGroupsError('');
    try {
      const response = await listGroups();
      setGroups(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      setGroupsError(error?.message || 'No se pudo obtener el listado de grupos.');
    } finally {
      setGroupsLoading(false);
    }
  }, [canManageGroups]);

  const fetchRoles = useCallback(async () => {
    if (!canManageGroups) {
      return;
    }
    setRolesLoading(true);
    try {
      const response = await listRoles();
      setRoles(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      console.warn('⚠️ No se pudo cargar el listado de roles:', error);
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, [canManageGroups]);

  const fetchPolicies = useCallback(async () => {
    if (!canManageGroups) {
      return;
    }
    setPoliciesLoading(true);
    try {
      const response = await listPolicies();
      setPolicies(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      console.warn('⚠️ No se pudo cargar el listado de políticas:', error);
      setPolicies([]);
    } finally {
      setPoliciesLoading(false);
    }
  }, [canManageGroups]);

  const fetchUsers = useCallback(async () => {
    if (!canManageGroups) {
      return;
    }
    try {
      const response = await listUsers();
      setUsers(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      console.warn('⚠️ No se pudo cargar el listado de usuarios:', error);
      setUsers([]);
    }
  }, [canManageGroups]);

  useEffect(() => {
    fetchGroups();
    fetchRoles();
    fetchPolicies();
    fetchUsers();
  }, [fetchGroups, fetchRoles, fetchPolicies, fetchUsers]);

  const rolesById = useMemo(() => {
    const map = {};
    roles.forEach((role) => { map[role.role_id] = role; });
    return map;
  }, [roles]);

  const policiesById = useMemo(() => {
    const map = {};
    policies.forEach((policy) => { map[policy.policy_id] = policy; });
    return map;
  }, [policies]);

  const memberCountByGroupId = useMemo(() => {
    const counts = {};
    users.forEach((user) => {
      (Array.isArray(user.group_ids) ? user.group_ids : []).forEach((groupId) => {
        counts[groupId] = (counts[groupId] || 0) + 1;
      });
    });
    return counts;
  }, [users]);

  const totalMembersAcrossGroups = useMemo(
    () => Object.values(memberCountByGroupId).reduce((sum, count) => sum + count, 0),
    [memberCountByGroupId]
  );

  const handleOpenCreate = () => setDialogState({ open: true, mode: 'create', group: null });
  const handleOpenEdit = (group) => setDialogState({ open: true, mode: 'edit', group });
  const handleCloseDialog = () => {
    if (isSubmitting) return;
    setDialogState({ open: false, mode: 'create', group: null });
  };

  const handleSubmitDialog = async (formState) => {
    setIsSubmitting(true);
    setFeedback(null);
    try {
      if (dialogState.mode === 'edit' && dialogState.group) {
        await updateGroup(dialogState.group.group_id, formState);
        setFeedback({ severity: 'success', message: `Se actualizó el grupo "${formState.name}".` });
      } else {
        await createGroup(formState);
        setFeedback({ severity: 'success', message: `Se creó el grupo "${formState.name}".` });
      }
      setDialogState({ open: false, mode: 'create', group: null });
      await fetchGroups();
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'No se pudo guardar el grupo.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setFeedback(null);
    try {
      await deleteGroup(deleteTarget.group_id);
      setFeedback({ severity: 'success', message: `Se eliminó el grupo "${deleteTarget.name}".` });
      setDeleteTarget(null);
      await fetchGroups();
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'No se pudo eliminar el grupo.' });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!accessChecked) {
    return (
      <PageContainer title="Grupos" description="Organiza usuarios en grupos con roles y políticas compartidas.">
        <Section title="Cargando acceso">
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Verificando permisos...</Typography>
          </Stack>
        </Section>
      </PageContainer>
    );
  }

  if (!canManageGroups) {
    return (
      <PageContainer title="Grupos" description="Solo administradores de la cuenta pueden gestionar grupos de usuarios.">
        <Section title="Acceso restringido">
          <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}>
            Necesitas privilegios de administrador (de tu cuenta o de la plataforma) para acceder a esta sección.
          </Alert>
        </Section>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Grupos"
      description="Colecciones de usuarios con roles y/o políticas compartidas -- un usuario puede pertenecer a varios grupos a la vez."
      actions={[
        <Button
          key="refresh"
          variant="outlined"
          color="info"
          startIcon={<RefreshIcon />}
          onClick={() => { fetchGroups(); fetchRoles(); fetchPolicies(); fetchUsers(); }}
          disabled={groupsLoading}
          sx={{ textTransform: 'none', borderColor: 'var(--wb-accent-40)', color: 'var(--wb-accent)' }}
        >
          Actualizar
        </Button>,
        <Button
          key="group"
          variant="outlined"
          color="secondary"
          onClick={handleOpenCreate}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Nuevo grupo
        </Button>
      ]}
    >
      {feedback && (
        <Box sx={{ mb: 3 }}>
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        </Box>
      )}

      <StatGrid
        items={[
          { label: 'Grupos configurados', value: String(groups.length), trend: 'Sin límite por cuenta' },
          { label: 'Membresías activas', value: String(totalMembersAcrossGroups), trend: 'Suma de todos los grupos' }
        ]}
      />

      {groupsError && (
        <Alert severity="error" sx={{ mb: 2, backgroundColor: 'var(--wb-danger-18)', color: 'var(--wb-danger-soft)' }}>
          {groupsError}
        </Alert>
      )}

      <Section
        title="Grupos de la cuenta"
        subtitle="Asigna la membresía de cada usuario desde la sección Usuarios"
      >
        {groupsLoading && groups.length === 0 && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={22} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Cargando grupos...</Typography>
          </Stack>
        )}

        {!groupsLoading && groups.length === 0 && !groupsError && (
          <Typography sx={{ color: 'var(--wb-muted-70)', py: 2 }}>
            No hay grupos configurados todavía.
          </Typography>
        )}

        <Grid container spacing={3}>
          {groups.map((group, index) => {
            const roleIds = Array.isArray(group.role_ids) ? group.role_ids : [];
            const policyIds = Array.isArray(group.policy_ids) ? group.policy_ids : [];
            const memberCount = memberCountByGroupId[group.group_id] || 0;
            return (
              <Grid item xs={12} md={4} key={group.group_id}>
                <Box
                  sx={{
                    backgroundColor: 'var(--wb-bg-surface)',
                    borderRadius: 2,
                    border: '1px solid var(--wb-border-10)',
                    p: 3,
                    height: '100%'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {GROUP_ICONS[index % GROUP_ICONS.length]}
                      <Box>
                        <Typography sx={{ color: 'var(--wb-text-title)', fontWeight: 600 }}>{group.name}</Typography>
                        <Typography sx={{ color: 'var(--wb-muted-76)', fontSize: '0.85rem' }}>
                          {group.description}
                        </Typography>
                        <Typography sx={{ color: 'var(--wb-muted-60)', fontSize: '0.78rem', mt: 0.5 }}>
                          {memberCount} {memberCount === 1 ? 'miembro' : 'miembros'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Editar grupo">
                        <IconButton size="small" onClick={() => handleOpenEdit(group)} sx={{ color: 'var(--wb-accent)' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar grupo">
                        <IconButton size="small" onClick={() => setDeleteTarget(group)} sx={{ color: 'var(--wb-danger)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                    {roleIds.length === 0 && policyIds.length === 0 && (
                      <Typography sx={{ color: 'var(--wb-muted-60)', fontSize: '0.8rem' }}>
                        Sin roles ni políticas adjuntas
                      </Typography>
                    )}
                    {roleIds.map((roleId) => (
                      <Chip
                        key={`role-${roleId}`}
                        label={rolesById[roleId]?.name || roleId}
                        size="small"
                        sx={{ backgroundColor: 'var(--wb-x-bg-38bdf829, rgba(56, 189, 248, 0.16))', color: 'var(--wb-x-text-7dd3fc, #7dd3fc)', width: 'fit-content' }}
                      />
                    ))}
                    {policyIds.map((policyId) => (
                      <Chip
                        key={`policy-${policyId}`}
                        label={policiesById[policyId]?.name || policyId}
                        size="small"
                        sx={{ backgroundColor: 'var(--wb-border-16)', color: 'var(--wb-text-label)', width: 'fit-content' }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Section>

      <GroupFormDialog
        open={dialogState.open}
        mode={dialogState.mode}
        initialGroup={dialogState.group}
        roles={roles}
        rolesLoading={rolesLoading}
        policies={policies}
        policiesLoading={policiesLoading}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        isSubmitting={isSubmitting}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (isDeleting ? undefined : setDeleteTarget(null))}
        PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface)', border: '1px solid var(--wb-border-16)' } }}
      >
        <DialogTitle sx={{ color: 'var(--wb-text-title)' }}>Eliminar grupo</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--wb-text-label)' }}>
            ¿Seguro que deseas eliminar el grupo "{deleteTarget?.name}"? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting} sx={{ color: 'var(--wb-muted)' }}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={isDeleting}>
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default SecurityGroups;
